import { Fragment, useEffect, useState } from 'react';

import capitalize from 'lodash/capitalize';

import {
  Button,
  ButtonProps,
  Divider,
  Indicator,
  useDrawer,
  useDrawerInstance,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

import { useNodeQuery } from 'hooks/queries/nodes';

import { NodeWorkloadsSubDrawer } from './_components/NodeWorkloadsDrawer';
import { useNodeIssuesDrawer } from './_hooks/useNodeIssuesDrawer';
import { IssuesIcon } from '../../../_components/IssuesIcon';
import { NodeActionsMenu } from '../../_components/NodeActionsMenu';
import {
  NodeAttributesDrawer,
  type NodeAttribute,
} from '../../_components/NodeAttributesDrawer';
import { NodeDetailsSubDrawer, NodeWithDependencies } from '../../types';

import { isAnywhereCluster } from '@cast/shared-cluster-context-data-access';

type Props = {
  cluster: Cluster;
  node: NodeWithDependencies;
  masterNodesCount: number;
  initialSubdrawer?: NodeDetailsSubDrawer;
};

const attributes = [
  'labels',
  'annotations',
  'taints',
] satisfies NodeAttribute[];

const ActionButton = ({
  isActive,
  ...props
}: ButtonProps & { isActive: boolean }) => {
  return (
    <Button
      sx={
        isActive
          ? {
              border: tokens.theme.border.structural.neutralStrong,
              backgroundColor: 'grey.100',
            }
          : undefined
      }
      variant="ghost"
      size="sm"
      {...props}
    />
  );
};

export const NodeDetailsDrawerActions = ({
  node,
  cluster,
  masterNodesCount,
  initialSubdrawer,
}: Props) => {
  const { node: _nodeDetails, isLoading: isLoadingNodeDetails } = useNodeQuery({
    clusterId: cluster.id,
    nodeId: node.id,
    enabled: !isAnywhereCluster(cluster),
  });

  const nodeDetails = isAnywhereCluster(cluster) ? node : _nodeDetails;

  const { id, subDrawerOpen } = useDrawerInstance();
  const [activeSubDrawer, setActiveSubDrawer] = useState<
    NodeDetailsSubDrawer | undefined
  >(undefined);
  const { open } = useDrawer();

  const handleOpenNodeAttributesDrawer = (attribute: NodeAttribute) => {
    if (nodeDetails) {
      setActiveSubDrawer(attribute);
      open({
        component: NodeAttributesDrawer,
        props: {
          node: nodeDetails,
          nodeAttribute: attribute,
        },
        options: { parentId: id },
      });
    }
  };

  const { openNodeIssuesDrawer } = useNodeIssuesDrawer();

  const handeOpenIssuesSubDrawer = () => {
    setActiveSubDrawer('issues');
    openNodeIssuesDrawer(node);
  };

  const handeOpenWorkloadsSubDrawer = () => {
    setActiveSubDrawer('workloads');
    open({
      component: NodeWorkloadsSubDrawer,
      props: {
        // TODO (Justas): Drawer expects something else. Fix cast
        workloads: node.workloads!,
      },
      options: { parentId: id },
    });
  };

  useEffect(() => {
    if (initialSubdrawer === 'workloads') {
      handeOpenWorkloadsSubDrawer();
    } else if (initialSubdrawer === 'issues') {
      handeOpenIssuesSubDrawer();
    } else if (initialSubdrawer) {
      handleOpenNodeAttributesDrawer(initialSubdrawer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubdrawer]);

  return (
    <Stack direction="row" gap={2} mr={-12} justifyContent="flex-end">
      <ActionButton
        disabled={!node.workloads?.length}
        endIcon={
          node.workloads?.length ? (
            <Indicator
              color="grey"
              count={node.workloads?.length}
              size="small"
            />
          ) : null
        }
        isActive={subDrawerOpen && activeSubDrawer === 'workloads'}
        onClick={handeOpenWorkloadsSubDrawer}
        testId="open-subdrawer-workloads"
      >
        Workloads
      </ActionButton>

      <Divider orientation="vertical" variant="middle" flexItem />

      {attributes.map((attribute) => (
        <Fragment key={attribute}>
          <ActionButton
            disabled={isLoadingNodeDetails}
            isActive={subDrawerOpen && activeSubDrawer === attribute}
            onClick={() => handleOpenNodeAttributesDrawer(attribute)}
            testId={`open-subdrawer-${attribute}`}
          >
            {capitalize(attribute)}
          </ActionButton>
          <Divider orientation="vertical" variant="middle" flexItem />
        </Fragment>
      ))}
      {!!node?.isFailedToDrain && (
        <ActionButton
          disabled={isLoadingNodeDetails}
          startIcon={<IssuesIcon />}
          isActive={subDrawerOpen && activeSubDrawer === 'issues'}
          onClick={handeOpenIssuesSubDrawer}
          testId="open-subdrawer-issues"
        />
      )}
      <NodeActionsMenu
        cluster={cluster}
        node={node}
        masterNodesCount={masterNodesCount}
      />
    </Stack>
  );
};
